import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, CheckCircle, AlertTriangle, AlertCircle, Send, Calendar, Upload, FileUp, X, Printer, RefreshCw, Trash2 } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import AppHeader from '../components/AppHeader';

// Safe date formatting helper to prevent white screen crashes
const safeFormatDate = (dateValue, formatStr, fallback = '-') => {
  if (!dateValue) return fallback;
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return fallback;
    return format(date, formatStr);
  } catch (e) {
    console.warn('Date formatting error:', e, dateValue);
    return fallback;
  }
};

const enconOrange = '#ee7203';

const DiscrepancyReport = ({ user }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [sendingToWarehouse, setSendingToWarehouse] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigneeName, setAssigneeName] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showAdjustmentPrompt, setShowAdjustmentPrompt] = useState(false);
  const [refreshingM4, setRefreshingM4] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Free stock upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hasTodaysStock, setHasTodaysStock] = useState(true);
  
  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/pi-counts/discrepancy/${reportId}`);
      const reportData = response.data.report;
      setReport(reportData);
      setItems(reportData?.items || []);
      setHasTodaysStock(reportData?.has_todays_stock || false);
      
      // Show upload modal if no today's stock report
      if (!reportData?.has_todays_stock) {
        setShowUploadModal(true);
      }
    } catch (error) {
      console.error('Error loading discrepancy report:', error);
      toast.error('Failed to load discrepancy report');
    } finally {
      setLoading(false);
    }
  }, [reportId]);
  
  useEffect(() => {
    loadReport();
  }, [loadReport]);
  
  // Drag and drop handlers for the entire page
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!showUploadModal) {
        setIsDragging(true);
      }
    };
    
    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    };
    
    // Add listeners to the entire document
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);
    
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [showUploadModal]);
  
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setShowUploadModal(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`${API}/pi-counts/upload-stock`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      toast.success('Free Stock Report uploaded successfully');
      setShowUploadModal(false);
      setHasTodaysStock(true);
      
      // Reload the report to get updated data
      await loadReport();
    } catch (error) {
      console.error('Error uploading stock:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  const handleRecountChange = (itemIndex, value) => {
    setItems(prev => prev.map((item, idx) => {
      if (idx === itemIndex) {
        const recount = value === '' ? null : parseFloat(value) || 0;
        // New discrepancy is based on current free stock (from today's report) minus on_the_road
        const currentExpected = item.on_hand_quantity ?? item.current_free_stock ?? item.original_expected ?? 0;
        const onRoad = item.on_the_road || 0;
        return {
          ...item,
          recount_value: recount,
          new_discrepancy: recount !== null ? recount - currentExpected + onRoad : null
        };
      }
      return item;
    }));
  };

  const handleOnRoadChange = (itemIndex, value) => {
    setItems(prev => prev.map((item, idx) => {
      if (idx === itemIndex) {
        const onRoad = value === '' ? 0 : parseFloat(value) || 0;
        // Recalculate new discrepancy with on_the_road
        const currentExpected = item.on_hand_quantity ?? item.current_free_stock ?? item.original_expected ?? 0;
        const recount = item.recount_value;
        return {
          ...item,
          on_the_road: onRoad,
          new_discrepancy: recount !== null && recount !== undefined ? recount - currentExpected + onRoad : null
        };
      }
      return item;
    }));
  };
  
  const saveProgress = async () => {
    try {
      setSaving(true);
      await api.put(`${API}/pi-counts/discrepancy/${reportId}/items`, {
        items: items.map(item => ({
          ...item,
          recount_qty: item.recount_value,
        }))
      });
      toast.success('Progress saved');
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  // Refresh M4 On Hand Data
  const refreshM4Data = async () => {
    try {
      setRefreshingM4(true);
      // Use the dedicated discrepancy report refresh endpoint
      const response = await api.post(`${API}/pi-counts/discrepancy/${reportId}/refresh-m4-data`);
      toast.success(response.data.message || 'M4 data refreshed successfully');
      // Reload the report to get updated on_hand values
      await loadReport();
    } catch (error) {
      console.error('Error refreshing M4 data:', error);
      toast.error(error.response?.data?.detail || 'Failed to refresh M4 data');
    } finally {
      setRefreshingM4(false);
    }
  };

  // Delete discrepancy report (super_admin only)
  const handleDeleteReport = async () => {
    try {
      setDeleting(true);
      await api.delete(`${API}/pi-counts/discrepancy/${reportId}`);
      toast.success('Discrepancy report deleted');
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete report');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  // Print discrepancy report
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Discrepancy Report - ${report?.supplier_name}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm; }
          body { font-family: Arial, sans-serif; font-size: 11px; }
          .header { text-align: center; margin-bottom: 15px; }
          .header h1 { color: #dc2626; font-size: 22px; margin: 0 0 5px 0; }
          .header .subtitle { font-size: 14px; color: #ee7203; font-weight: bold; }
          .header .date { font-size: 13px; color: #666; margin-top: 5px; }
          .name-field { margin-bottom: 15px; }
          .name-field label { font-weight: bold; font-size: 12px; }
          .name-field .line { border-bottom: 1px solid #333; width: 250px; display: inline-block; margin-left: 10px; height: 18px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { background: #dc2626; color: white; padding: 8px 6px; text-align: left; font-size: 10px; }
          td { padding: 10px 6px; border-bottom: 1px solid #ccc; vertical-align: middle; }
          tr:nth-child(even) { background: #fafafa; }
          .desc-cell { width: 35%; }
          .unit-cell { width: 8%; text-align: center; }
          .count-cell { width: 12%; text-align: center; }
          .recount-cell { width: 15%; }
          .recount-box { border: 1px solid #888; height: 28px; background: white; border-radius: 3px; }
          .notes-cell { width: 20%; }
          .notes-box { border: 1px solid #888; height: 28px; background: white; border-radius: 3px; }
          .footer { margin-top: 15px; text-align: center; font-size: 9px; color: #999; }
          .diff-negative { color: #dc2626; font-weight: bold; }
          .diff-positive { color: #22c55e; font-weight: bold; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            tr { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Discrepancy Report</h1>
          <div class="subtitle">${report?.supplier_name || ''}</div>
          <div class="date">${report?.date ? format(new Date(report.date), 'EEEE, dd MMMM yyyy') : ''}</div>
        </div>
        
        <div class="name-field">
          <label>Recounted By:</label>
          <span class="line"></span>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="desc-cell">Product Description</th>
              <th class="unit-cell">Unit</th>
              <th class="count-cell">System</th>
              <th class="count-cell">Counted</th>
              <th class="count-cell">Diff</th>
              <th class="recount-cell">Recount</th>
              <th class="notes-cell">Notes</th>
            </tr>
          </thead>
          <tbody>
            ${items.map(item => {
              const onHandQty = item.on_hand_quantity ?? item.current_free_stock ?? item.expected_quantity ?? 0;
              const diff = (item.actual_quantity || 0) - onHandQty;
              const diffClass = diff < 0 ? 'diff-negative' : diff > 0 ? 'diff-positive' : '';
              return `
                <tr>
                  <td class="desc-cell">${item.description || item.product_name || item.product_code || ''}</td>
                  <td class="unit-cell">${item.stock_unit || ''}</td>
                  <td class="count-cell">${onHandQty}</td>
                  <td class="count-cell">${item.actual_quantity || 0}</td>
                  <td class="count-cell ${diffClass}">${diff > 0 ? '+' : ''}${diff}</td>
                  <td class="recount-cell"><div class="recount-box"></div></td>
                  <td class="notes-cell"><div class="notes-box"></div></td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          ${items.length} Discrepancy Items | Generated ${format(new Date(), 'dd/MM/yyyy HH:mm')}
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };
  
  const sendToWarehouse = () => {
    setAssigneeName('');
    setShowAssignModal(true);
  };

  const confirmSendToWarehouse = async () => {
    try {
      setShowAssignModal(false);
      setSendingToWarehouse(true);
      await saveProgress();
      await api.put(`${API}/pi-counts/discrepancy/${reportId}/send-to-warehouse`, {
        assigned_to: assigneeName.trim()
      });
      toast.success('Sent to warehouse for recounting');
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error sending to warehouse:', error);
      toast.error('Failed to send to warehouse');
    } finally {
      setSendingToWarehouse(false);
    }
  };
  
  const completeReport = async (createAdjustment = false) => {
    try {
      setCompleting(true);
      await saveProgress();
      await api.put(`${API}/pi-counts/discrepancy/${reportId}/complete`, {
        final_items: items,
        action: createAdjustment ? 'create_adjustment' : 'complete'
      });
      
      if (createAdjustment) {
        // Create an adjustment report for the discrepancies
        const itemsWithDiscrepancy = items.filter(item => {
          const newCount = item.recount_value;
          const expected = item.current_free_stock ?? item.original_expected ?? 0;
          const disc = newCount !== null && newCount !== undefined ? newCount - expected : item.discrepancy;
          return Math.abs(disc) > 0.01;
        });
        
        if (itemsWithDiscrepancy.length > 0) {
          try {
            const adjResponse = await api.post(`${API}/pi-counts/adjustment-reports/create`, {
              count_id: reportId,
              count_name: `Discrepancy Recount - Week ${report?.week_commencing || ''}`,
              count_date: report?.week_commencing || new Date().toISOString().split('T')[0],
              count_type: 'discrepancy',
              items: itemsWithDiscrepancy.map(item => ({
                product_code: item.product_code,
                product_name: item.product_name,
                expected_qty: item.current_free_stock ?? item.original_expected ?? 0,
                counted_qty: item.recount_value ?? item.original_count ?? 0,
                discrepancy: item.new_discrepancy ?? item.discrepancy ?? 0,
                on_the_road: item.on_the_road || 0
              }))
            });
            toast.success('Discrepancy report completed - Adjustment report created');
            setShowAdjustmentPrompt(false);
            setShowCompleteModal(false);
            // Navigate to the adjustment report
            if (adjResponse.data?.report_id) {
              navigate(`/pi-counts/adjustment/${adjResponse.data.report_id}`);
            } else {
              navigate('/pi-counts');
            }
            return;
          } catch (adjError) {
            console.error('Error creating adjustment report:', adjError);
            toast.error('Failed to create adjustment report');
          }
        } else {
          toast.info('No items with discrepancy to adjust - Report completed');
        }
      } else {
        toast.success('Discrepancy report completed - No adjustment required');
      }
      
      setShowAdjustmentPrompt(false);
      setShowCompleteModal(false);
      navigate('/pi-counts');
    } catch (error) {
      console.error('Error completing report:', error);
      toast.error('Failed to complete report');
    } finally {
      setCompleting(false);
    }
  };
  
  const subHeaderLeft = (
    <>
      <button
        onClick={() => navigate('/pi-counts')}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
        style={{ color: enconOrange }}
        data-testid="discrepancy-report-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Back</span>
      </button>
      <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <AlertTriangle className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
        <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Discrepancy Report</span>
      </div>
    </>
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
        <div className="flex items-center justify-center h-screen">
          <p className="text-lg text-gray-600">Loading discrepancy report...</p>
        </div>
      </div>
    );
  }
  
  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
        <div className="flex items-center justify-center h-screen">
          <p className="text-lg text-red-600">Discrepancy report not found</p>
        </div>
      </div>
    );
  }
  
  const recountedItems = items.filter(item => item.recount_value !== null && item.recount_value !== undefined).length;
  
  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 ${isDragging ? 'ring-4 ring-inset ring-orange-400' : ''}`}>
      <AppHeader user={user} subHeaderLeft={subHeaderLeft} />
      
      {/* Drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-orange-100 bg-opacity-90 z-50 flex items-center justify-center">
          <div className="text-center">
            <FileUp className="w-20 h-20 mx-auto mb-4" style={{ color: enconOrange }} />
            <p className="text-2xl font-bold" style={{ color: enconOrange }}>
              Drop Free Stock Report Here
            </p>
            <p className="text-gray-600 mt-2">Release to upload stock CSV file</p>
          </div>
        </div>
      )}
      
      <div className="px-4 lg:px-8 py-6" style={{ paddingTop: '164px' }}>
        <div className="max-w-[1400px] mx-auto">
        {/* Stock Warning Banner */}
        {!hasTodaysStock && (
          <div 
            className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg flex items-center justify-between cursor-pointer hover:bg-yellow-100"
            onClick={() => setShowUploadModal(true)}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-800">Free Stock Report Required</p>
                <p className="text-sm text-yellow-700">
                  Upload today&apos;s GFST0000.csv to see current expected quantities
                </p>
              </div>
            </div>
            <Button
              onClick={(e) => { e.stopPropagation(); setShowUploadModal(true); }}
              style={{ backgroundColor: enconOrange, color: 'white' }}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
        )}
        
        {/* Report Header */}
        <Card className="mb-6 bg-white border-2 border-red-200">
          <CardHeader className="bg-red-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-7 h-7" />
                  Discrepancy Report
                </CardTitle>
                <div className="flex items-center gap-2 mt-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">
                    Week Commencing: {report.week_commencing && safeFormatDate(report.week_commencing, 'dd MMMM yyyy')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Created: {report.created_at && safeFormatDate(report.created_at, 'dd/MM/yyyy HH:mm')}
                </p>
                {report.latest_valuation_date && (
                  <p className="text-xs text-gray-500 mt-1">
                    Latest Stock Data: {report.latest_valuation_date}
                  </p>
                )}
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  report.status === 'completed' ? 'bg-green-100 text-green-800' :
                  report.status === 'at_warehouse' ? 'bg-indigo-100 text-indigo-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {report.status === 'at_warehouse' ? 'At Warehouse' : report.status}
                </span>
                <p className="text-sm text-gray-600 mt-2">
                  {recountedItems} of {items.length} recounted
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>
        
        {/* Action Buttons */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <Button
            onClick={refreshM4Data}
            disabled={refreshingM4}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="refresh-m4-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshingM4 ? 'animate-spin' : ''}`} />
            {refreshingM4 ? 'Refreshing...' : 'Refresh M4 Data'}
          </Button>
          
          <Button
            onClick={saveProgress}
            disabled={saving}
            variant="outline"
            style={{ borderColor: enconOrange, color: enconOrange }}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Progress'}
          </Button>
          
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            style={{ borderColor: enconOrange, color: enconOrange }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Stock
          </Button>
          
          <Button
            onClick={handlePrint}
            variant="outline"
            style={{ borderColor: enconOrange, color: enconOrange }}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          
          {report.status !== 'at_warehouse' && report.status !== 'completed' && (
            <Button
              onClick={sendToWarehouse}
              disabled={sendingToWarehouse}
              variant="outline"
              className="border-indigo-500 text-indigo-600 hover:bg-indigo-50"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingToWarehouse ? 'Sending...' : 'Send to Warehouse'}
            </Button>
          )}
          
          <Button
            onClick={() => setShowCompleteModal(true)}
            disabled={recountedItems === 0}
            style={{ backgroundColor: '#22c55e', color: 'white' }}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Complete Report
          </Button>
          
          {/* Delete button - Super Admin only, positioned to the right */}
          {user?.role === 'super_admin' && (
            <div className="flex-1 flex justify-end">
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </div>
          )}
        </div>
        
        {/* Items Table */}
        <Card className="bg-white border-2 border-red-300">
          <CardHeader className="bg-red-100">
            <CardTitle className="text-lg text-red-700">
              Discrepancy Items ({items.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase bg-gray-50">Code</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase bg-gray-50">Description</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-yellow-700 uppercase bg-yellow-100">Original Count</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-yellow-700 uppercase bg-yellow-100">Original Discrepancy</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-blue-700 uppercase bg-blue-100">
                      Current Expected
                      <span className="block text-[10px] font-normal text-blue-500">(On Hand Qty)</span>
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-blue-700 uppercase bg-blue-100">New Count</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-blue-700 uppercase bg-blue-100">On Road</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-red-700 uppercase bg-red-100">New Discrepancy</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(items) && items.map((item, index) => {
                    // Defensive check: ensure item is an object
                    if (!item || typeof item !== 'object') return null;
                    
                    const productCode = String(item.product_code || item.code || 'Unknown');
                    const productName = String(item.product_name || item.description || '');
                    const stockUnit = String(item.stock_unit || '');
                    const hasRecount = item.recount_value !== null && item.recount_value !== undefined;
                    const currentExpected = typeof (item.on_hand_quantity ?? item.current_free_stock ?? item.original_expected) === 'number' 
                      ? (item.on_hand_quantity ?? item.current_free_stock ?? item.original_expected) 
                      : 0;
                    const newDisc = hasRecount ? (item.recount_value - currentExpected) : null;
                    const originalCount = typeof (item.original_count ?? item.counted_qty) === 'number'
                      ? (item.original_count ?? item.counted_qty)
                      : 0;
                    const discrepancy = typeof item.discrepancy === 'number' ? item.discrepancy : 0;
                    
                    return (
                      <tr 
                        key={item.product_id || `item-${index}`}
                        className="border-b hover:bg-gray-100 bg-white"
                      >
                        <td className="px-3 py-3 text-sm font-medium">{productCode}</td>
                        <td className="px-3 py-3 text-sm text-gray-600 max-w-xs truncate" title={productName}>
                          {productName || <span className="text-gray-400 italic">No description</span>}
                        </td>
                        <td className="px-3 py-3 text-sm text-right font-semibold bg-yellow-50">
                          {originalCount.toFixed(2)}
                          {stockUnit && <span className="text-xs text-gray-500 ml-1">{stockUnit}</span>}
                        </td>
                        <td className={`px-3 py-3 text-sm text-right font-semibold bg-yellow-50 ${
                          discrepancy < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {discrepancy >= 0 ? '+' : ''}{discrepancy.toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right font-semibold bg-blue-50">
                          {currentExpected.toFixed(2)}
                          {!hasTodaysStock && <span className="text-xs text-yellow-600 ml-1">*</span>}
                        </td>
                        <td className="px-3 py-3 bg-blue-50">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.recount_value === null || item.recount_value === undefined ? '' : item.recount_value}
                            onChange={(e) => handleRecountChange(index, e.target.value)}
                            placeholder="Enter count"
                            className="text-center w-24"
                          />
                        </td>
                        <td className="px-3 py-3 bg-blue-50">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.on_the_road || ''}
                            onChange={(e) => handleOnRoadChange(index, e.target.value)}
                            placeholder="0"
                            className="text-center w-20"
                          />
                        </td>
                        <td className={`px-3 py-3 text-sm text-right font-bold bg-red-50 ${
                          hasRecount 
                            ? ((item.new_discrepancy ?? newDisc) >= 0 ? 'text-green-600' : 'text-red-600')
                            : 'text-gray-400'
                        }`}>
                          {hasRecount 
                            ? `${(item.new_discrepancy ?? newDisc) >= 0 ? '+' : ''}${(item.new_discrepancy ?? newDisc).toFixed(2)}`
                            : '-'
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!hasTodaysStock && (
              <div className="p-3 bg-yellow-50 border-t text-xs text-yellow-700">
                * Expected values may be outdated. Upload today&apos;s Free Stock Report for accurate figures.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
      
      {/* Upload Free Stock Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent
          className="max-w-lg rounded-2xl"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: `3px solid ${enconOrange}`
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ color: enconOrange }}>
              <Upload className="w-6 h-6" />
              Upload Free Stock Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!hasTodaysStock && (
              <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                <p className="text-sm text-yellow-800 font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  No stock report uploaded today
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Upload stock CSV to get accurate expected quantities for recounting.
                </p>
              </div>
            )}
            
            {uploading ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Uploading...</span>
                  <span className="text-sm text-gray-500">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${uploadProgress}%`,
                      background: `linear-gradient(90deg, ${enconOrange}, #f97316)`
                    }}
                  />
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                  id="stock-upload-discrepancy"
                />
                <label
                  htmlFor="stock-upload-discrepancy"
                  className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg cursor-pointer hover:bg-orange-50 transition-colors"
                  style={{ borderColor: enconOrange }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = e.dataTransfer?.files;
                    if (files && files.length > 0) {
                      handleFileUpload(files[0]);
                    }
                  }}
                >
                  <FileUp className="w-12 h-12 mb-3" style={{ color: enconOrange }} />
                  <p className="text-sm font-medium" style={{ color: enconOrange }}>
                    Drop file here or click to upload
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    GFST0000.csv format
                  </p>
                </label>
              </div>
            )}
            
            <div className="flex justify-end">
              <Button
                onClick={() => setShowUploadModal(false)}
                variant="outline"
              >
                {hasTodaysStock ? 'Close' : 'Skip for now'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Complete Confirmation Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid #22c55e'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-green-600 flex items-center gap-2">
              <CheckCircle className="w-6 h-6" />
              Complete Discrepancy Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to complete this discrepancy report?
            </p>
            
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm">
                <span className="font-semibold">{recountedItems}</span> of <span className="font-semibold">{items.length}</span> items have been recounted.
              </p>
              {recountedItems < items.length && (
                <p className="text-xs text-orange-600 mt-1">
                  Warning: Some items have not been recounted yet.
                </p>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowCompleteModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowCompleteModal(false);
                  setShowAdjustmentPrompt(true);
                }}
                disabled={completing}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                Yes, Complete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Adjustment Required Prompt */}
      <Dialog open={showAdjustmentPrompt} onOpenChange={setShowAdjustmentPrompt}>
        <DialogContent
          className="max-w-md rounded-2xl"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid #f97316'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-orange-600 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" />
              Adjustment Required?
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Do you need to create a stock adjustment for this discrepancy report?
            </p>
            
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                <span className="font-semibold">Yes</span> - Creates an adjustment report and takes you to the Adjustments page
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <span className="font-semibold">No</span> - Saves the discrepancy under the month view
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => completeReport(false)}
                disabled={completing}
                variant="outline"
                className="flex-1 border-gray-400"
              >
                {completing ? 'Processing...' : 'No, Save Only'}
              </Button>
              <Button
                onClick={() => completeReport(true)}
                disabled={completing}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                {completing ? 'Processing...' : 'Yes, Create Adjustment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent 
          className="bg-white"
          style={{ borderRadius: '16px' }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <Trash2 className="w-6 h-6" />
              Delete Discrepancy Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this discrepancy report? This action cannot be undone.
            </p>
            
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                <span className="font-semibold">Report:</span> {report?.supplier_name}
              </p>
              <p className="text-sm text-red-700 mt-1">
                <span className="font-semibold">Items:</span> {items.length} discrepancy items
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                variant="outline"
                className="flex-1 border-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteReport}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Deleting...' : 'Delete Report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign to Warehouse Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-indigo-600">Send to Warehouse</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Assign count to
              </label>
              <Input
                data-testid="assign-to-input"
                value={assigneeName}
                onChange={(e) => setAssigneeName(e.target.value)}
                placeholder="Enter name..."
                className="w-full"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                Leave blank if there is no need to assign this count to a specific person.
              </p>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button
                data-testid="cancel-assign-btn"
                variant="outline"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-send-warehouse-btn"
                onClick={confirmSendToWarehouse}
                disabled={sendingToWarehouse}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingToWarehouse ? 'Sending...' : 'Send to W/H'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscrepancyReport;
